import type { TransitAspect } from "./types/natal-overview";

export enum LifeAspects {
  InnerWorld = "self",
  Relationships = "socialLife",
  Creativity = "creativity",
  Romance = "romance",
  Health = "health",
  Spirituality = "spirituality",
}


export enum Planets {
  Moon = "moon",
  Mercury = "mercury",
  Uranus = "uranus",
  Neptune = "neptune",
  Venus = "venus",
  Mars = "mars",
  Sun = "sun",
  Jupiter = "jupiter",
  Saturn = "saturn",
  Pluto = "pluto",
  Asc = "ascendant",
  NorthNode = "north_node"
}

export enum ConjunctionFlavors {
  Hard = "conj_hard",
  Soft = "conj_soft"
}

export enum Aspects {
  Opposition = "opposition",
  Square = "square",
  Quincunx = "quincunx",
  SemiSquare = "semisquare",
  SesquiQuadrate = "sesquiquadrate",
  Trine = "trine",
  Sextile = "sextile",
  Conjunction = "conjunction",
}

export enum Signs {
  Aries = "aries",
  Taurus = "taurus",
  Gemini = "gemini",
  Cancer = "cancer",
  Leo = "leo",
  Virgo = "virgo",
  Libra = "libra",
  Scorpio = "scorpio",
  Sagittarius = "sagittarius",
  Capricorn = "capricorn",
  Aquarius = "aquarius",
  Pisces = "pisces",
}

export type NatalMarkers = {
  sign: Signs;
  house: number;
}

export type NatalProfile = {
  planets: { [key in Planets]: NatalMarkers; }
  houseSigns: { [key: number]: Signs; };
}

export type AspectsPerPlanet = {
  [key in Planets]?: TransitAspect[];
}

export const DefaultNatalProfile: NatalProfile = {
  planets: {
    [Planets.Venus]: {
      house: 1,
      sign: Signs.Aquarius
    },
    [Planets.Jupiter]: {
      house: 1,
      sign: Signs.Pisces
    },
    [Planets.Mars]: {
      house: 1,
      sign: Signs.Libra
    },
    [Planets.Mercury]: {
      house: 1,
      sign: Signs.Gemini
    },
    [Planets.Moon]: {
      house: 1,
      sign: Signs.Taurus
    },
    [Planets.Neptune]: {
      house: 1,
      sign: Signs.Aquarius
    },
    [Planets.Pluto]: {
      house: 1,
      sign: Signs.Virgo
    },
    [Planets.Saturn]: {
      house: 1,
      sign: Signs.Sagittarius
    },
    [Planets.Sun]: {
      house: 1,
      sign: Signs.Aquarius
    },
    [Planets.Uranus]: {
      house: 1,
      sign: Signs.Cancer
    },
    [Planets.NorthNode]: {
      house: 2,
      sign: Signs.Leo
    },
    [Planets.Asc]: {
      house: 3,
      sign: Signs.Scorpio
    }
  },
  houseSigns: {}
} as const

export const SignsInOrder = [
  Signs.Aries,
  Signs.Taurus,
  Signs.Gemini,
  Signs.Cancer,
  Signs.Leo,
  Signs.Virgo,
  Signs.Libra,
  Signs.Scorpio,
  Signs.Sagittarius,
  Signs.Capricorn,
  Signs.Aquarius,
  Signs.Pisces
] as const

export const NatalTraits = {
  [Planets.Asc]: [
    "key to self-confidence",
    "shows your window to the world"
  ],
  [Planets.Sun]: [
    "key to full self-expression",
    "shows power and talent",
    "your individual",
    "creative style",
    "self expression and leadership"
  ],
  [Planets.Moon]: [
    "key to emotional satisfaction",
    "shows what you need for emotional security",
    "closeness and intimacy"
  ],
  [Planets.Mercury]: [
    "key to rewarding communication",
    "signifies your talents for verbal communication",
    "thinking process"
  ],
  [Planets.Venus]: [
    "key to love and self-worth",
    "points out the way for harmony in relationship",
    "self-worth",
    "love language"
  ],
  [Planets.Mars]: [
    "key to personal assertiveness",
    "reveals your leadership energy"
  ],
  [Planets.Jupiter]: [
    "key to social opportunity and reward",
    "shows how to build trust and faith",
    "how to intuitive receiving from the Universe"
  ],
  [Planets.Saturn]: [
    "key to public achievement",
    "show the process of personal achievement"
  ],
  [Planets.Uranus]: [
    "key to aliveness",
    "shows your uniqueness",
    "your innovation",
    "the freedom point"
  ],
  [Planets.Neptune]: [
    "key to emotional ecstasy",
    "removes the illusion",
    "reveals the truth",
    "shows your path of spirituality"
  ],
  [Planets.Pluto]: [
    "key to fearlessness and empowerment",
    "reveals where you most covertly resist change"
  ],
  [Planets.NorthNode]: [
    "key to self-happiness"
  ]
} as const

export const SignRulers = {
  [Signs.Aquarius]: Planets.Uranus,
  [Signs.Aries]: Planets.Mars,
  [Signs.Taurus]: Planets.Venus,
  [Signs.Gemini]: Planets.Mercury,
  [Signs.Cancer]: Planets.Moon,
  [Signs.Leo]: Planets.Sun,
  [Signs.Virgo]: Planets.Mercury,
  [Signs.Libra]: Planets.Venus,
  [Signs.Scorpio]: Planets.Pluto,
  [Signs.Sagittarius]: Planets.Jupiter,
  [Signs.Capricorn]: Planets.Saturn,
  [Signs.Pisces]: Planets.Neptune
} as const

export interface AnglularData {
  angle: number;
  orb: number
}

export type AspectStronomicalData = {
  [key in Aspects]: AnglularData;
}

export const AspectsData: AspectStronomicalData = {
  [Aspects.Opposition]: { angle: 180, orb: 3 },
  [Aspects.Square]: { angle: 90, orb: 3 },
  [Aspects.Quincunx]: { angle: 150, orb: 3 },
  [Aspects.SemiSquare]: { angle: 45, orb: 3 },
  [Aspects.SesquiQuadrate]: { angle: 135, orb: 3 },
  [Aspects.Trine]: { angle: 120, orb: 6 },
  [Aspects.Sextile]: { angle: 60, orb: 6 },
  [Aspects.Conjunction]: { angle: 0, orb: 4 }
} as const

export enum Element {
  Fire = "fire",
  Earth = "earth",
  Air = "air",
  Water = "water"
}

export const SignElement = {
  [Signs.Aries]: Element.Fire,
  [Signs.Taurus]: Element.Earth,
  [Signs.Gemini]: Element.Air,
  [Signs.Cancer]: Element.Water,
  [Signs.Leo]: Element.Fire,
  [Signs.Virgo]: Element.Earth,
  [Signs.Libra]: Element.Air,
  [Signs.Scorpio]: Element.Water,
  [Signs.Sagittarius]: Element.Fire,
  [Signs.Capricorn]: Element.Earth,
  [Signs.Aquarius]: Element.Air,
  [Signs.Pisces]: Element.Water
} as const

export const NorthNodeThemes = Object.freeze({
  [Signs.Aries]: {
    "default": "claiming_independence",
    "by_house": {
      "1": "claiming_independence",
      "2": "steadying_self_worth",
      "3": "speaking_up",
      "4": "creating_own_foundation",
      "5": "expressing_creativity",
      "6": "taking_the_lead_in_daily_life",
      "7": "balancing_me_and_we",
      "8": "facing_intimacy_and_power",
      "9": "exploring_truth",
      "10": "stepping_into_leadership",
      "11": "asserting_in_community",
      "12": "facing_hidden_self"
    }
  },
  [Signs.Taurus]: {
    "default": "grounding_in_body",
    "by_house": {
      "1": "embodying_self_worth",
      "2": "grounding_in_body",
      "3": "speaking_from_values",
      "4": "building_safe_home",
      "5": "finding_pleasure_in_expression",
      "6": "creating_sustainable_routines",
      "7": "seeking_stable_partnerships",
      "8": "stabilizing_shared_resources",
      "9": "trusting_slow_growth_in_vision",
      "10": "building_slow_and_steady_reputation",
      "11": "valuing_supportive_community",
      "12": "releasing_scarcity_fears"
    }
  },
  [Signs.Gemini]: {
    "default": "speaking_up",
    "by_house": {
      "1": "showing_curiosity_openly",
      "2": "experimenting_with_resources",
      "3": "speaking_up",
      "4": "naming_family_stories",
      "5": "playing_with_ideas_in_love",
      "6": "adapting_routines",
      "7": "learning_through_dialogue_in_relationships",
      "8": "asking_hard_questions_about_intimacy",
      "9": "challenging_beliefs_through_conversation",
      "10": "communicating_clearly_in_career",
      "11": "sharing_ideas_with_networks",
      "12": "giving_words_to_the_unconscious"
    }
  },
  [Signs.Cancer]: {
    "default": "opening_heart",
    "by_house": {
      "1": "honoring_sensitivity",
      "2": "nurturing_self_worth",
      "3": "emotional_honesty_in_conversation",
      "4": "healing_family_roots",
      "5": "opening_heart_in_joy",
      "6": "bringing_care_into_daily_life",
      "7": "creating_safe_partnerships",
      "8": "emotional_depth_in_intimacy",
      "9": "finding_belonging_in_beliefs",
      "10": "leading_with_care",
      "11": "nurturing_community",
      "12": "allowing_emotional_release"
    }
  },
  [Signs.Leo]: {
    "default": "stepping_into_visibility",
    "by_house": {
      "1": "owning_presence",
      "2": "confident_self_worth",
      "3": "sharing_your_voice_boldly",
      "4": "shining_within_family",
      "5": "stepping_into_visibility",
      "6": "taking_pride_in_daily_work",
      "7": "showing_up_authentically_in_love",
      "8": "courage_in_vulnerability",
      "9": "bold_beliefs_and_visions",
      "10": "being_seen_in_career",
      "11": "creative_role_in_community",
      "12": "releasing_fear_of_being_seen"
    }
  },
  [Signs.Virgo]: {
    "default": "refining_service",
    "by_house": {
      "1": "living_with_intentionality",
      "2": "practical_self_worth",
      "3": "clarifying_thinking",
      "4": "organizing_inner_and_outer_home",
      "5": "refining_creative_expression",
      "6": "refining_service",
      "7": "bringing_discernment_to_relationships",
      "8": "discerning_energy_exchanges",
      "9": "refining_belief_systems",
      "10": "crafting_your_contribution",
      "11": "useful_role_in_community",
      "12": "releasing_perfectionism"
    }
  },
  [Signs.Libra]: {
    "default": "allowing_support",
    "by_house": {
      "1": "seeing_self_through_relationship",
      "2": "balancing_give_and_take_in_resources",
      "3": "diplomacy_in_communication",
      "4": "creating_balance_at_home",
      "5": "seeking_harmony_in_love",
      "6": "collaborative_daily_life",
      "7": "balancing_me_and_we",
      "8": "fairness_in_intimacy",
      "9": "balancing_truth_with_perspective",
      "10": "relational_style_in_career",
      "11": "building_fair_community",
      "12": "releasing_people_pleasing"
    }
  },
  [Signs.Scorpio]: {
    "default": "embracing_depth",
    "by_house": {
      "1": "owning_intensity",
      "2": "transforming_values",
      "3": "speaking_truth_beneath_the_surface",
      "4": "facing_family_depths",
      "5": "embracing_depth_in_desire",
      "6": "deep_work_and_purification",
      "7": "intense_honesty_in_relationships",
      "8": "embracing_depth",
      "9": "seeking_transformation_in_beliefs",
      "10": "wielding_power_responsibly",
      "11": "deep_bonds_in_community",
      "12": "befriending_the_shadows"
    }
  },
  [Signs.Sagittarius]: {
    "default": "exploring_truth",
    "by_house": {
      "1": "living_big_and_open",
      "2": "expansive_view_of_resources",
      "3": "speaking_frankly",
      "4": "finding_truth_in_roots",
      "5": "adventurous_joy",
      "6": "freedom_within_routines",
      "7": "honest_and_open_relationships",
      "8": "seeking_truth_in_intimacy",
      "9": "exploring_truth",
      "10": "visionary_career_path",
      "11": "ideals_in_community",
      "12": "trusting_life_beyond_control"
    }
  },
  [Signs.Capricorn]: {
    "default": "stepping_into_leadership",
    "by_house": {
      "1": "owning_responsibility",
      "2": "building_long_term_security",
      "3": "structured_communication",
      "4": "maturing_family_patterns",
      "5": "commitment_to_creativity",
      "6": "discipline_in_daily_life",
      "7": "serious_commitment_in_relationships",
      "8": "maturity_in_shared_resources",
      "9": "structured_belief_systems",
      "10": "stepping_into_leadership",
      "11": "responsibility_to_community",
      "12": "releasing_over_control"
    }
  },
  [Signs.Aquarius]: {
    "default": "serving_collective",
    "by_house": {
      "1": "owning_difference",
      "2": "unusual_values",
      "3": "innovative_thinking",
      "4": "nontraditional_roots",
      "5": "authentic_self_expression",
      "6": "unique_approach_to_work",
      "7": "unconventional_relationships",
      "8": "detachment_in_intimacy",
      "9": "future_oriented_beliefs",
      "10": "serving_collective_through_role",
      "11": "serving_collective",
      "12": "releasing_fear_of_belonging"
    }
  },
  [Signs.Pisces]: {
    "default": "trusting_intuition",
    "by_house": {
      "1": "living_with_permeable_boundaries",
      "2": "softening_attachment_to_material",
      "3": "intuitive_communication",
      "4": "mystical_roots",
      "5": "creative_surrender",
      "6": "gentle_service",
      "7": "compassion_in_relationships",
      "8": "merging_in_intimacy",
      "9": "faith_based_beliefs",
      "10": "subtle_influence_in_career",
      "11": "spiritual_community",
      "12": "trusting_intuition"
    }
  }
})
